export const GROK_MEDIA_GATEWAY_SHA = "255b2814660eb69c356cc1a910d8b083eeca37f2";
export const GROK_MEDIA_VERIFIED_AT = "2026-08-04";
/**
 * `grok-imagine-image-2.0` 的溯源与上面四个**不同源**，所以单列而不是复用常量。
 *
 * 上面四个是 2026-08-04 读**网关源码** `255b2814` 核出来的；本 SKU 的参数面来自
 * ByteSpike 已发布清单 v51+（`published_model_catalogs`，运维 2026-08-27 对着 xAI
 * 官方文档策展），由本仓在 2026-08-29 直读生产清单 + `docs.x.ai/developers/models`
 * 两处对撞确认。sha 记的是**当时在跑的网关构建**（`llm-gateway:f8d94b48`），不是
 * 「读了这个 commit 的源码」——两种溯源混进同一个常量会让以后没人分得清哪条是实读的。
 */
const GROK_IMAGE_20_GATEWAY_SHA = "f8d94b48f5fec341c6278f79b15ccb1839aca211";
const GROK_IMAGE_20_VERIFIED_AT = "2026-08-29";
export const GROK_IMAGE_ASPECT_RATIOS = [
    "1:1",
    "3:4",
    "4:3",
    "9:16",
    "16:9",
    "2:3",
    "3:2",
    "9:19.5",
    "19.5:9",
    "9:20",
    "20:9",
    "1:2",
    "2:1",
    "auto",
];
export const GROK_VIDEO_ASPECT_RATIOS = ["1:1", "16:9", "9:16", "4:3", "3:4", "3:2", "2:3"];
export const GROK_MEDIA_CANONICAL_MODEL_IDS = [
    "grok-imagine-image-2.0",
    "grok-imagine-image",
    "grok-imagine-image-quality",
    "grok-imagine-video",
    "grok-imagine-video-1.5",
];
/**
 * 兼容别名 → canonical。**别名指向哪个 canonical 由上游实际映射决定，不由「哪个是当前
 * 推荐款」决定。**
 *
 * `grok-imagine` / `grok-imagine-edit` 仍指 `-quality`，即便 ByteSpike 已把 `-quality`
 * 移出策展菜单：生产 `usage_logs` 里这两个 id 的 `upstream_model` 逐条都是
 * `grok-imagine-image-quality`（2026-08-29 直读，3 条）。把它们改指 `-2.0` 会让一个写
 * `grok-imagine` 的旧配置**静默换成另一个模型**——正是红线 #11 禁的那种改写。别名过期
 * 的正确表现是「解析到一个当前清单里没有的 canonical」，由 {@link deriveImageGenModels}
 * 的落空告警暴露，不是在这里偷偷改目标。
 */
const MEDIA_ALIASES = {
    "grok-imagine": "grok-imagine-image-quality",
    "grok-imagine-edit": "grok-imagine-image-quality",
    "grok-imagine-video-1.5-preview": "grok-imagine-video-1.5",
    "grok-imagine-video-1.5-2026-05-30": "grok-imagine-video-1.5",
};
const CANONICAL_IDS = new Set(GROK_MEDIA_CANONICAL_MODEL_IDS);
const IMAGE_N = Array.from({ length: 10 }, (_, index) => String(index + 1));
const VIDEO_DURATION = Array.from({ length: 15 }, (_, index) => String(index + 1));
const EXTENSION_DURATION = Array.from({ length: 9 }, (_, index) => String(index + 2));
const IMAGE_CONSTRAINTS = {
    editInputMax: 3,
    storageErrorPolicy: "partial_failure",
};
const VIDEO_BASE_CONSTRAINTS = {
    durationSeconds: { min: 1, max: 15, default: 8 },
    referenceImagesMax: 7,
    imageAndReferencesMutuallyExclusive: true,
    editInputDurationSecondsMax: 8.7,
    extensionInputDurationSeconds: { min: 2, max: 15 },
    extensionDurationSeconds: { min: 2, max: 10, default: 6 },
    asyncTerminalRequired: true,
    terminalSuccessStatus: "done",
    terminalFailureStatuses: ["failed", "expired"],
    hideOutputControlsForModes: ["edit", "extension"],
    storageErrorPolicy: "partial_failure",
};
const VIDEO_15_CONSTRAINTS = {
    durationSeconds: { min: 1, max: 15, default: 8 },
    referenceImagesMax: 7,
    imageAndReferencesMutuallyExclusive: true,
    asyncTerminalRequired: true,
    terminalSuccessStatus: "done",
    terminalFailureStatuses: ["failed", "expired"],
    storageErrorPolicy: "partial_failure",
};
const DEFAULT_PROVENANCE = {
    verifiedAt: GROK_MEDIA_VERIFIED_AT,
    sourceGatewaySha: GROK_MEDIA_GATEWAY_SHA,
};
function baseCapability(canonicalModelId, kind, constraints, provenance = DEFAULT_PROVENANCE) {
    return {
        provider: "grok",
        kind,
        canonicalModelId,
        paramsVerified: true,
        verifiedAt: provenance.verifiedAt,
        sourceGatewaySha: provenance.sourceGatewaySha,
        constraints,
    };
}
function imageContract(canonicalModelId, provenance = DEFAULT_PROVENANCE) {
    return {
        ...baseCapability(canonicalModelId, "image", IMAGE_CONSTRAINTS, provenance),
        transport: "grok_images",
        operations: ["generate", "edit"],
        paramSchema: {
            operation: ["generate", "edit"],
            aspect_ratio: [...GROK_IMAGE_ASPECT_RATIOS],
            resolution: ["1k", "2k"],
            n: [...IMAGE_N],
            response_format: ["url", "b64_json"],
            input_type: ["url", "data_url", "file_id", "multipart"],
        },
    };
}
function videoContract(canonicalModelId) {
    const isBase = canonicalModelId === "grok-imagine-video";
    const modes = isBase
        ? ["text_to_video", "image_to_video", "reference_to_video", "edit", "extension"]
        : ["text_to_video", "image_to_video", "reference_to_video"];
    return {
        ...baseCapability(canonicalModelId, "video", isBase ? VIDEO_BASE_CONSTRAINTS : VIDEO_15_CONSTRAINTS),
        transport: "grok_videos",
        operations: isBase ? ["generate", "edit", "extension"] : ["generate"],
        paramSchema: {
            mode: modes,
            aspect_ratio: [...GROK_VIDEO_ASPECT_RATIOS],
            resolution: isBase ? ["480p", "720p"] : ["480p", "720p", "1080p"],
            duration: [...VIDEO_DURATION],
            ...(isBase ? { extension_duration: [...EXTENSION_DURATION] } : {}),
            input_type: ["url", "data_url", "file_id"],
        },
    };
}
const CONTRACTS = {
    /**
     * xAI 当前推荐的图片 SKU；ByteSpike 清单 v51（2026-08-28）起是该网关**唯一**的 Grok
     * 图片行，取代了 `grok-imagine-image` 与 `-quality`（注意：那两个在 **xAI 官方仍然在售**，
     * 下架只是 ByteSpike 的策展决定，别在文案里写成「上游下线了」）。
     *
     * 参数面与 `-quality` 逐字段相同（operation / 14 档 aspect_ratio / 1k·2k / n 1–10 /
     * response_format / input_type 含 multipart），所以复用 {@link imageContract}。
     *
     * ⚠️ `editInputMax: 3` 是**沿用**而不是官方数字：xAI 未公布本 SKU 的参考图上限（网关
     * 清单 notes 明确写了这点，且刻意留空没从旧模型抄）。这里填 3 是为了让契约与运行时
     * **实际执行的**上限一致——`runGrokImageGeneration` 与 image-tools 两处都硬编码了 3，
     * 契约填 undefined 只会让元数据和行为对不上。等 xAI 公布了再按官方值改。
     */
    "grok-imagine-image-2.0": imageContract("grok-imagine-image-2.0", {
        verifiedAt: GROK_IMAGE_20_VERIFIED_AT,
        sourceGatewaySha: GROK_IMAGE_20_GATEWAY_SHA,
    }),
    "grok-imagine-image": imageContract("grok-imagine-image"),
    "grok-imagine-image-quality": imageContract("grok-imagine-image-quality"),
    "grok-imagine-video": videoContract("grok-imagine-video"),
    "grok-imagine-video-1.5": videoContract("grok-imagine-video-1.5"),
};
export function canonicalizeGrokMediaModelId(modelId) {
    const trimmed = modelId.trim();
    if (CANONICAL_IDS.has(trimmed))
        return trimmed;
    return MEDIA_ALIASES[trimmed];
}
export function getGrokMediaModelContract(modelId) {
    const canonical = canonicalizeGrokMediaModelId(modelId);
    return canonical ? CONTRACTS[canonical] : undefined;
}
/**
 * Attach the verified contract to one existing catalog row. Unknown rows are returned unchanged;
 * known aliases keep their original id so catalog ingestion never changes entitlement identity.
 */
export function enrichGrokMediaCatalogEntry(entry) {
    const contract = getGrokMediaModelContract(entry.modelId);
    if (!contract)
        return entry;
    const { paramSchema, ...mediaGeneration } = contract;
    return {
        ...entry,
        capabilities: {
            ...entry.capabilities,
            paramSchema: Object.fromEntries(Object.entries(paramSchema).map(([key, values]) => [key, [...values]])),
            paramsVerified: true,
            mediaGeneration: {
                ...mediaGeneration,
                operations: [...mediaGeneration.operations],
                constraints: { ...mediaGeneration.constraints },
            },
        },
    };
}
/**
 * Derive the admin/runtime media candidates from already-entitled catalog rows. Compatibility aliases
 * collapse to one canonical row, preferring a true canonical row when both are present. No row is created
 * unless at least one matching catalog entitlement already exists, and the selected row's group is retained.
 */
export function resolveCanonicalGrokMediaCatalog(entries) {
    const byCanonical = new Map();
    for (const entry of entries) {
        const canonical = canonicalizeGrokMediaModelId(entry.modelId);
        if (!canonical)
            continue;
        const rows = byCanonical.get(canonical) ?? [];
        rows.push(entry);
        byCanonical.set(canonical, rows);
    }
    const output = [];
    for (const canonical of GROK_MEDIA_CANONICAL_MODEL_IDS) {
        const rows = byCanonical.get(canonical);
        if (!rows?.length)
            continue;
        const selected = rows.find((row) => row.modelId === canonical) ?? rows[0];
        const enriched = enrichGrokMediaCatalogEntry(selected);
        output.push({ ...enriched, modelId: canonical });
    }
    return output;
}
//# sourceMappingURL=media-generation-contract.js.map